import type { FeatureFlags } from '@better-lights/core';
import { useShallow } from 'zustand/react/shallow';
import { useAccount } from '../account/store.ts';
import { requiredFeature } from '../api/errors.ts';
import { setUi } from '../state/uiStore.ts';
import { cheapestPlanWith, type FeatureFlag, flagsFor, has } from './features.ts';
import { loadPlans, usePlans } from './store.ts';

/** Flags of the given workspace (default: the active one). */
export function currentFlags(scope?: string | null): FeatureFlags {
  const { me, activeScope } = useAccount.getState();
  return flagsFor(me, scope ?? activeScope);
}

export function useFlags(scope?: string | null): FeatureFlags {
  const { me, activeScope } = useAccount(useShallow((s) => ({ me: s.me, activeScope: s.activeScope })));
  return flagsFor(me, scope ?? activeScope);
}

/** Opens the paywall pre-selected on the cheapest plan with `flag`. */
export function openPaywallFor(flag: FeatureFlag, plan?: string, orgId?: string): void {
  setUi({
    paywall: {
      reason: 'feature',
      feature: flag,
      plan: plan ?? cheapestPlanWith(flag, usePlans.getState().catalog),
      orgId,
    },
  });
  if (plan) return;
  void loadPlans().then((c) => {
    setUi({ paywall: { reason: 'feature', feature: flag, plan: cheapestPlanWith(flag, c) } });
  });
}

/** True if the workspace has `flag`; otherwise opens the paywall and returns false. */
export function requireFeature(flag: FeatureFlag, scope?: string | null): boolean {
  if (has(currentFlags(scope), flag)) return true;
  openPaywallFor(flag);
  return false;
}

/** Opens the paywall for a 402 `feature_required`. Returns true when it handled the error. */
export function handleFeatureError(e: unknown, scope?: string | null): boolean {
  const f = requiredFeature(e);
  if (!f) return false;
  const me = useAccount.getState().me;
  // A company workspace upgrades through the Team plan, not a personal one.
  const org = scope && me && scope !== me.personalScope ? scope : undefined;
  openPaywallFor(f as FeatureFlag, org ? 'team' : undefined, org);
  return true;
}
